import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import { ArtworkCard } from "@/components/ArtworkCard";
import { ArrowLeft } from "lucide-react";

export const CollectionPage = () => {
  const { collectionId } = useParams();
  const [collection, setCollection] = useState(null);
  const [artworks, setArtworks] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [collectionRes, artworksRes] = await Promise.all([
          axios.get(`${API}/collections/${collectionId}`),
          axios.get(`${API}/artworks?collection_id=${collectionId}`),
        ]);
        setCollection(collectionRes.data);
        setArtworks(artworksRes.data);
      } catch (e) {
        console.error("Error fetching collection:", e);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [collectionId]);

  const seasonColors = {
    spring: "bg-spring",
    summer: "bg-summer",
    fall: "bg-fall",
    winter: "bg-winter",
  };

  if (loading) {
    return (
      <div className="py-12 md:py-16 px-6 md:px-12 lg:px-24 max-w-screen-2xl mx-auto">
        <div className="h-[40vh] bg-muted animate-pulse mb-12" />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 md:gap-12">
          {[1, 2, 3].map((i) => (
            <div key={i} className="aspect-[3/4] bg-muted animate-pulse" />
          ))}
        </div>
      </div>
    );
  }

  if (!collection) {
    return (
      <div className="py-24 text-center" data-testid="collection-not-found">
        <p className="text-muted-foreground mb-4">Collection not found</p>
        <Link to="/shop" className="text-primary hover:underline">
          Back to Shop
        </Link>
      </div>
    );
  }

  return (
    <div data-testid="collection-page">
      {/* Hero */}
      <section className="relative h-[40vh] md:h-[50vh] overflow-hidden">
        <img
          src={collection.cover_image}
          alt={collection.name}
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-black/40" />
        
        <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-6">
          <span className={`${seasonColors[collection.season]} px-4 py-2 text-xs uppercase tracking-widest text-foreground mb-4`}>
            {collection.season} {collection.year}
          </span>
          <h1 className="font-serif text-4xl md:text-6xl text-white font-medium tracking-tight">
            {collection.name}
          </h1>
          <p className="mt-4 text-white/80 max-w-xl">
            {collection.description}
          </p>
        </div>
      </section>

      {/* Artworks */}
      <section className="py-16 md:py-24 px-6 md:px-12 lg:px-24 max-w-screen-2xl mx-auto">
        <Link 
          to="/shop" 
          className="inline-flex items-center gap-2 text-sm uppercase tracking-widest text-muted-foreground hover:text-primary mb-12"
          data-testid="back-to-shop"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Shop
        </Link>

        <p className="text-sm text-muted-foreground mb-8">
          {artworks.length} {artworks.length === 1 ? "artwork" : "artworks"} in this collection
        </p>

        {artworks.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-muted-foreground">No artwork in this collection yet.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 md:gap-12">
            {artworks.map((artwork, index) => (
              <ArtworkCard key={artwork.id} artwork={artwork} index={index} />
            ))}
          </div>
        )}
      </section>
    </div>
  );
};

export default CollectionPage;
